import { useAtomValue } from 'jotai';
import { domain } from 'modules/data';
import {
    netSuiteExpenseItemsState,
    netSuiteItemLinesState,
} from 'pages/requestForm/containers/netsuite/resources/states';
import { useCallback, useMemo } from 'react';
import { useToggle } from 'react-use';
import { useGetNetSuiteBillMatching, useGetNetSuitePurchaseOrderMatching } from 'shared/data/webApp/v2';
import { useNetSuiteMatchingSuggestions } from 'shared/hooks/(matchings)';
import { netSuiteBillFieldValuesState } from 'shared/states';

import { MatchingInfoParams } from './NetSuiteMatchingInfo.types';

export const useSourceLine = (
    request: domain.Request,
    lineType: domain.NetSuiteMatchingLineType,
    lineNumber: number,
    isRequestForm: boolean
) => {
    const expenseItems = useAtomValue(netSuiteExpenseItemsState);
    const itemLines = useAtomValue(netSuiteItemLinesState);

    return useMemo(() => {
        if (
            request.integrationCode !== domain.IntegrationCode.NetSuiteBill &&
            request.integrationCode !== domain.IntegrationCode.NetSuitePO
        ) {
            return null;
        }

        const sourceLine =
            lineType === domain.NetSuiteMatchingLineType.Expense
                ? (isRequestForm ? expenseItems : request.details.expenseLines).find(
                      (line) => line.lineNumber === lineNumber
                  )
                : (isRequestForm ? itemLines : request.details.itemLines).find(
                      (line) => line.lineNumber === lineNumber
                  );

        return sourceLine ?? null;
    }, [expenseItems, itemLines, lineNumber, lineType, request.integrationCode, request.details, isRequestForm]);
};

const useNetSuiteMatchingInfo = (
    requestId: string,
    companyId: string,
    integrationCode: domain.IntegrationCode | null,
    isEnabled: boolean
) => {
    const { data: netSuiteBillMatching, isFetching: isNetSuiteBillMatchingFetching } = useGetNetSuiteBillMatching(
        {
            path: {
                companyId,
                vendorBillRequestId: requestId,
            },
        },
        { enabled: isEnabled && integrationCode === domain.IntegrationCode.NetSuiteBill }
    );

    const { data: netSuitePurchaseOrderMatching, isFetching: isNetSuitePurchaseOrderMatchingFetching } =
        useGetNetSuitePurchaseOrderMatching(
            {
                path: {
                    companyId,
                    purchaseOrdersRequestId: requestId,
                },
            },
            { enabled: isEnabled && integrationCode === domain.IntegrationCode.NetSuitePO }
        );

    return {
        matchingInfo: netSuiteBillMatching || netSuitePurchaseOrderMatching,
        isFetching: isNetSuiteBillMatchingFetching || isNetSuitePurchaseOrderMatchingFetching,
    };
};

export const useNetSuiteMatchingInfoDropdown = (isOpenedDefault: boolean = false) => {
    const [isOpened, toggleIsOpened] = useToggle(isOpenedDefault);

    const onDropdownClose = useCallback(() => toggleIsOpened(false), [toggleIsOpened]);

    return {
        isOpened,
        onDropdownClose,
        toggleIsOpened,
    };
};

export const useMatchingInfo = ({
    request,
    isOpened,
    isDraftMatching,
    lineNumber,
    lineType,
    targetRequestId,
    itemId,
    accountId,
    categoryId,
}: MatchingInfoParams) => {
    const { matchingInfo, isFetching: isFetchingMatchingInfo } = useNetSuiteMatchingInfo(
        request.id,
        request.companyId,
        request.integrationCode,
        isOpened && !isDraftMatching
    );

    const { suggestions, isFetching: isFetchingSuggestions } = useNetSuiteMatchingSuggestions({
        request,
        lineType,
        itemId,
        accountId,
        categoryId,
        lineNumber: !isDraftMatching ? lineNumber : undefined,
        isEnabled: isOpened && isDraftMatching,
    });

    return useMemo(() => {
        if (!isDraftMatching) {
            return {
                matchingInfo,
                isFetching: isFetchingMatchingInfo,
            };
        }

        const matchingLinesInfos = suggestions?.matchingLinesInfos.filter((line) => line.requestId === targetRequestId);

        return {
            matchingInfo: matchingLinesInfos?.length ? { matchingLinesInfos } : undefined,
            isFetching: isFetchingSuggestions,
        };
    }, [isDraftMatching, matchingInfo, isFetchingMatchingInfo, suggestions, isFetchingSuggestions, targetRequestId]);
};

export const useIsAddingMatchingAllowed = (
    request: domain.Request,
    sourceLine: domain.NetSuiteExpenseLine | domain.NetSuiteItemLine | null,
    isRequestForm: boolean
) => {
    const billFields = useAtomValue(netSuiteBillFieldValuesState);

    if (request.integrationCode !== domain.IntegrationCode.NetSuiteBill || request.details.internalId || !sourceLine) {
        return false;
    }

    if (!isRequestForm) {
        return [
            domain.RequestStatusV2.OnApproval,
            domain.RequestStatusV2.OnReview,
            domain.RequestStatusV2.OnHold,
        ].includes(request.statusV2);
    }

    return Boolean(
        billFields.vendor &&
            billFields.currency &&
            (('account' in sourceLine && sourceLine.account && 'category' in sourceLine && sourceLine.category) ||
                ('item' in sourceLine && sourceLine.item))
    );
};
